/**
 * Cleo ERP SaaS — Logs webhooks Paddle admin
 */
import React, { useEffect, useState } from 'react';
import { Card, Table, Tag, Typography, Select, Space, Alert, Badge } from 'antd';
import api from '../../../api/axios';

const { Title, Text } = Typography;

const PROCESSING_COLORS = {
  PROCESSED: '#10B981', FAILED: '#EF4444', IGNORED: '#94A3B8',
};

function BillingEventListPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ status: '', event_type: '' });
  const [failedCount, setFailedCount] = useState(0);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.event_type) params.event_type = filters.event_type;
      const [eventsRes, statsRes] = await Promise.all([
        api.get('/api/admin/billing/events/', { params }),
        api.get('/api/admin/billing/stats/'),
      ]);
      setEvents(eventsRes.data);
      setFailedCount(statsRes.data.failed_webhook_events_24h || 0);
    } catch {
      setError('Impossible de charger les événements.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [filters]);

  const eventTypes = [
    'subscription.created', 'subscription.updated', 'subscription.canceled',
    'transaction.completed', 'transaction.payment_failed',
  ];

  const columns = [
    {
      title: 'Type',
      dataIndex: 'event_type',
      key: 'event_type',
      render: (t) => <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{t}</Text>,
    },
    {
      title: 'Tenant',
      dataIndex: 'tenant_slug',
      key: 'tenant_slug',
      render: (slug) => slug || <Text style={{ color: '#94A3B8' }}>—</Text>,
    },
    {
      title: 'Statut',
      dataIndex: 'processing_status',
      key: 'processing_status',
      render: (s) => (
        <Tag color={PROCESSING_COLORS[s] || '#CBD5E1'} style={{ borderRadius: 6 }}>
          {s}
        </Tag>
      ),
    },
    {
      title: 'Erreur',
      dataIndex: 'processing_error',
      key: 'processing_error',
      render: (err) => err
        ? <Text style={{ color: '#EF4444', fontSize: 12 }}>{err.substring(0, 80)}{err.length > 80 ? '…' : ''}</Text>
        : <Text style={{ color: '#94A3B8' }}>—</Text>,
    },
    {
      title: 'Reçu le',
      dataIndex: 'received_at',
      key: 'received_at',
      render: (d) => new Date(d).toLocaleString('fr-FR'),
    },
    {
      title: 'Traité le',
      dataIndex: 'processed_at',
      key: 'processed_at',
      render: (d) => d ? new Date(d).toLocaleString('fr-FR') : <Text style={{ color: '#94A3B8' }}>—</Text>,
    },
    {
      title: 'Event ID',
      dataIndex: 'event_id',
      key: 'event_id',
      render: (id) => (
        <Text copyable style={{ fontSize: 11, fontFamily: 'monospace', color: '#64748B' }}>
          {id}
        </Text>
      ),
    },
  ];

  if (error) return <Alert message={error} type="error" showIcon />;

  return (
    <div>
      <Space style={{ marginBottom: 16 }} align="center">
        <Title level={4} style={{ color: '#0F172A', margin: 0 }}>Webhooks Paddle</Title>
        {failedCount > 0 && (
          <Badge count={failedCount} overflowCount={99}>
            <Tag color="#EF4444" style={{ borderRadius: 6, marginLeft: 8 }}>
              {failedCount} échec(s) sur 24h
            </Tag>
          </Badge>
        )}
      </Space>

      {failedCount > 0 && (
        <Alert
          type="error"
          showIcon
          message={`${failedCount} webhook(s) en échec au cours des 24 dernières heures.`}
          style={{ marginBottom: 16, borderRadius: 8 }}
        />
      )}

      <Card size="small" style={{ borderRadius: 12, border: '1px solid #E5E7EB', marginBottom: 16 }}>
        <Space wrap>
          <Select
            placeholder="Statut traitement"
            style={{ width: 180 }}
            allowClear
            onChange={(val) => setFilters(f => ({ ...f, status: val || '' }))}
            options={[
              { value: 'PROCESSED', label: 'Traité' },
              { value: 'FAILED', label: 'Échec' },
              { value: 'IGNORED', label: 'Ignoré' },
            ]}
          />
          <Select
            placeholder="Type d'événement"
            style={{ width: 240 }}
            allowClear
            onChange={(val) => setFilters(f => ({ ...f, event_type: val || '' }))}
            options={eventTypes.map((t) => ({ value: t, label: t }))}
          />
        </Space>
      </Card>

      <Card style={{ borderRadius: 12, border: '1px solid #E5E7EB' }}>
        <Table
          columns={columns}
          dataSource={events}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 25, showSizeChanger: false }}
          size="small"
          rowClassName={(record) =>
            record.processing_status === 'FAILED' ? 'table-row-failed' : ''
          }
        />
      </Card>
    </div>
  );
}

export default BillingEventListPage;
